<template>
  <div>
    <cancel-modal
      modal-id="subscription-cancel-modal"
      :visible="showCancelModal"
      :title="$t('subscription.cancel_confirm_title')"
      :message="cancelMessage"
      :justification-label="$t('subscription.cancellation_reason')"
      :justification-placeholder="$t('subscription.cancel_reason_placeholder')"
      :ok-title="$t('action.confirm')"
      :cancel-title="$t('action.cancel')"
      @confirm="doCancel"
      @cancel="showCancelModal = false"
      @update:visible="showCancelModal = $event"/>

    <Grid
      ref="grid"
      :service="service"
      route_name="subscriptions"
      route_grid_path="/grid"
      order_column="id"
      :fetch-on-mounted="false">
      <template v-slot:filterZone>
        <div class="flex flex-wrap gap-3 items-end w-full">
          <b-button
            v-permission="'subscriptions.create'"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            @click="addNew">
            <feather-icon
              icon="PlusIcon"
              class="mr-50"/>
            <span class="align-middle">{{ $t('AddNew') }}</span>
          </b-button>
          <SubscriptionFilter
            v-model="filters"
            @change="handleFilterChange"/>
        </div>
      </template>
      <template v-slot:gridThead>
        <vs-th key="id" sort-key="id">{{ $t('subscription.id') }}</vs-th>
        <vs-th key="user" sort-key="user_id">{{ $t('subscription.user') }}</vs-th>
        <vs-th key="plan" sort-key="subscription_plan_id">{{ $t('subscription.plan') }}</vs-th>
        <vs-th key="status" sort-key="status">{{ $t('subscription.status') }}</vs-th>
        <vs-th key="started_at" sort-key="started_at">{{ $t('subscription.started_at') }}</vs-th>
        <vs-th key="ends_at" sort-key="ends_at">{{ $t('subscription.ends_at') }}</vs-th>
      </template>
      <template v-slot:gridData="props">
        <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in props.data">
          <vs-td width="5%" key="id">
            <div> {{ tr.id }} </div>
          </vs-td>
          <vs-td width="20%" key="user">
            <div> {{ tr.user ? tr.user.name : '-' }}</div>
          </vs-td>
          <vs-td width="20%" key="plan">
            <div> {{ tr.subscription_plan ? tr.subscription_plan.name : '-' }}</div>
          </vs-td>
          <vs-td width="15%" key="status">
            <vs-chip transparent :color="getStatusColor(tr.status)" class="text-base">{{ tr.status_label }}</vs-chip>
          </vs-td>
          <vs-td width="15%" key="started_at">
            <div> {{ formatDate(tr.started_at) }} </div>
          </vs-td>
          <vs-td width="15%" key="ends_at">
            <div> {{ formatDate(tr.ends_at) }} </div>
          </vs-td>
          <vs-td width="10%">
            <div class="flex flex-row gap-2 items-center">
              <feather-icon
                v-permission="'subscriptions.show'"
                icon="EyeIcon"
                class="h-5 w-5 stroke-current text-primary cursor-pointer"
                v-tooltip.top="{
                  content: $t('action.consult'),
                  delay: { show: 100, hide: 100 }
                }"
                @click="$router.push({ name: 'subscription-detail', params: { id: tr.id } })"
              />
              <feather-icon
                v-if="canEdit(tr)"
                v-permission="'subscriptions.edit'"
                icon="EditIcon"
                class="h-5 w-5 stroke-current text-warning cursor-pointer"
                v-tooltip="$t('action.edit')"
                @click="props.edit(props.data[indextr].id)"/>
              <feather-icon
                v-if="canCancel(tr)"
                v-permission="'subscriptions.cancel'"
                icon="XCircleIcon"
                class="h-5 w-5 stroke-current text-danger cursor-pointer"
                v-tooltip="$t('subscription.cancel_action')"
                @click="confirmCancel(tr)"/>
              <feather-icon
                v-if="canDelete(tr)"
                v-permission="'subscriptions.delete'"
                icon="Trash2Icon"
                class="h-5 w-5 stroke-current text-danger cursor-pointer"
                v-tooltip="$t('action.delete')"
                @click="props.destroy(props.data[indextr])"/>
            </div>
          </vs-td>
        </vs-tr>
      </template>
    </Grid>
  </div>
</template>

<script>
import { BButton } from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'

import CancelModal from '@/components/CancelModal.vue'
import SubscriptionFilter from '@/components/filters/SubscriptionFilter.vue'
import SubscriptionService from '@/services/api/SubscriptionService'

export default {
  directives: {
    Ripple,
  },
  components: {
    BButton,
    CancelModal,
    SubscriptionFilter
  },
  data: () => ({
    selected: [],
    service: null,
    showCancelModal: false,
    subscriptionToCancel: null,
    filters: {
      user_id: null,
      status: null
    }
  }),
  computed: {
    cancelMessage() {
      if (!this.subscriptionToCancel) return this.$t('subscription.cancel_confirm_text')
      const user = this.subscriptionToCancel.user?.name || '-'
      const plan = this.subscriptionToCancel.subscription_plan?.name || '-'
      return this.$t('subscription.cancel_confirm_text_management', { user, plan })
    }
  },
  methods: {
    addNew() {
      this.$router.push('/subscriptions/create')
    },
    handleFilterChange(filters) {
      this.filters = filters
      this.updateFilters()
    },
    updateFilters() {
      const customFilters = {}
      if (this.filters.user_id) {
        customFilters.user_id = this.filters.user_id
      }
      if (this.filters.status) {
        customFilters.status = this.filters.status
      }

      this.$refs.grid.custom_filters = []
      if (Object.keys(customFilters).length > 0) {
        this.$refs.grid.fillCustomFilters(
          Object.keys(customFilters).map(key => ({
            key,
            value: customFilters[key]
          }))
        )
      }

      this.$refs.grid.fetchGridData(true)
    },
    formatDate(val) {
      if (!val) return '-'
      const d = new Date(val)
      return d.toLocaleDateString('pt-BR')
    },
    canEdit(tr) {
      return tr.status !== 'CA'
    },
    canCancel(tr) {
      return tr.status === 'AC' || tr.status === 'PE'
    },
    canDelete(tr) {
      return tr.status !== 'CA'
    },
    getStatusColor(status) {
      const statusMap = {
        PE: 'warning',
        AC: 'success',
        CA: 'danger',
        EX: 'dark',
        SP: 'primary'
      }
      return statusMap[status] || 'default'
    },
    confirmCancel(tr) {
      this.subscriptionToCancel = tr
      this.showCancelModal = true
    },
    doCancel(data) {
      if (!this.subscriptionToCancel) return
      const id = this.subscriptionToCancel.id
      const reason = data?.justification || ''
      this.$vs.loading()
      this.service.cancel(id, reason).then(
        () => {
          this.notifySuccess(this.$vs, this.$t('messages.success'))
          this.showCancelModal = false
          this.subscriptionToCancel = null
          this.$refs.grid?.fetchGridData(true)
        },
        error => this.notifyError(this.$vs, error?.message || this.$t('messages.error'))
      ).finally(() => {
        this.$vs.loading.close()
      })
    }
  },
  beforeMount() {
    this.service = SubscriptionService.build(this.$vs)
  }
}
</script>
